/**
 * ReadySummary
 *
 * Unified send action footer combining status + send button.
 * Uses shared validation analysis for consistent messaging.
 * Shows re-render action when components are stale.
 */

import { ArrowPathIcon, PaperAirplaneIcon } from '@heroicons/react/20/solid';
import { AlertTriangleIcon, CheckCircleIcon, Loader2 } from 'lucide-react';
import React from 'react';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type {
  CorrespondenceChannel,
  Transmission,
} from '@/services/api/correspondenceService';

import type { PackageValidationStatus } from '../../types';
import {
  analyzeMissingFields,
  type MissingFieldAnalysis,
} from '../../utils/missingFieldAnalysis';
import {
  VALIDATION_VARIANT_STYLES,
  type ValidationVariant,
} from '../../utils/status';
import { CHANNEL_CONFIG } from '../constants';

export interface ReadySummaryProps {
  channel: CorrespondenceChannel;
  recipientName: string;
  /** Display string for destination (fax number, email, address) - empty if not set */
  destinationDisplay?: string | null;
  /** Full validation status for rich analysis */
  validationStatus?: PackageValidationStatus | null;
  /** Current transmission state for analysis */
  transmission?: Partial<Transmission> | null;
  /** Whether components have stale inputs and need re-rendering */
  hasStaleComponents?: boolean;
  /** Whether a render is currently in progress */
  isRendering?: boolean;
  /** Callback to trigger re-render */
  onRender?: () => void;
  /** Whether send is allowed */
  canSend?: boolean;
  /** Whether send is in progress */
  isSending?: boolean;
  /** Callback to send */
  onSend?: () => void;
  className?: string;
}

const STATUS_ICONS: Record<ValidationVariant, React.ElementType> = {
  ready: CheckCircleIcon,
  needs_action: AlertTriangleIcon,
  blocked: AlertTriangleIcon,
};

/** Get channel-specific prompt when destination is missing */
function getDestinationPrompt(channel: CorrespondenceChannel): string {
  switch (channel) {
    case 'fax':
      return 'Enter fax number above';
    case 'email':
      return 'Enter email address above';
    case 'mailed_letter':
    case 'mailed_cheque':
      return 'Enter mailing address above';
    default:
      return 'Enter destination above';
  }
}

/** Derive variant from analysis, stale state, and destination */
function getVariant(
  analysis: MissingFieldAnalysis | null,
  hasStaleComponents: boolean,
  hasDestination: boolean,
): ValidationVariant {
  if (!hasDestination) return 'needs_action';
  if (hasStaleComponents) return 'needs_action';
  if (!analysis) return 'ready';
  if (analysis.needsTransmission) return 'needs_action';
  if (analysis.needsClientInfo || analysis.issueCount > 0) return 'blocked';
  return 'ready';
}

/** Get status title - action-oriented, not duplicating ValidationBanner counts */
function getTitle(
  analysis: MissingFieldAnalysis | null,
  channel: CorrespondenceChannel,
  channelLabel: string,
  hasStaleComponents: boolean,
  isRendering: boolean,
  hasDestination: boolean,
): string {
  if (isRendering) return 'Generating preview...';
  if (!hasDestination) return getDestinationPrompt(channel);
  if (hasStaleComponents) return 'Preview needs update';
  if (!analysis || analysis.issueCount === 0) {
    return `Ready to send via ${channelLabel}`;
  }
  if (analysis.needsTransmission && analysis.transmissionCTA) {
    return analysis.transmissionCTA.text;
  }
  // Simple blocking status - ValidationBanner has the details
  return 'Missing required fields';
}

export const ReadySummary: React.FC<ReadySummaryProps> = ({
  channel,
  recipientName,
  destinationDisplay,
  validationStatus,
  transmission,
  hasStaleComponents = false,
  isRendering = false,
  onRender,
  canSend = false,
  isSending = false,
  onSend,
  className,
}) => {
  const { label: channelLabel } = CHANNEL_CONFIG[channel];
  const hasDestination = !!destinationDisplay;

  const analysis = validationStatus
    ? analyzeMissingFields(
        validationStatus,
        recipientName,
        transmission ?? null,
      )
    : null;

  const variant = getVariant(analysis, hasStaleComponents, hasDestination);
  const styles = VALIDATION_VARIANT_STYLES[variant];
  const StatusIcon = STATUS_ICONS[variant];
  const title = getTitle(
    analysis,
    channel,
    channelLabel,
    hasStaleComponents,
    isRendering,
    hasDestination,
  );

  const needsRerender = hasStaleComponents && !isRendering && hasDestination;
  const isReady =
    variant === 'ready' &&
    !hasStaleComponents &&
    !isRendering &&
    hasDestination;

  // Subtitle: show destination if available, otherwise show channel-specific hint
  const subtitle = hasDestination
    ? `${recipientName} · ${destinationDisplay}`
    : `Sending via ${channelLabel}`;

  return (
    <div className={cn('space-y-2', className)}>
      {/* Status row */}
      <div
        className={cn(
          'flex items-center gap-2 rounded-lg px-2.5 py-2',
          styles.bg,
        )}
      >
        {isRendering ? (
          <Loader2 className="h-3.5 w-3.5 flex-shrink-0 animate-spin text-blue-500" />
        ) : (
          <StatusIcon className={cn('h-3.5 w-3.5 flex-shrink-0', styles.icon)} />
        )}
        <div className="flex-1 min-w-0">
          <p className={cn('text-[11px] font-medium leading-tight', styles.text)}>{title}</p>
          <p className={cn('text-[10px] truncate leading-tight mt-0.5', styles.subtext)}>
            {subtitle}
          </p>
        </div>
      </div>

      {/* Action button - contextual based on state */}
      {needsRerender && onRender ? (
        <Button
          onClick={onRender}
          variant="outline"
          className="w-full h-11 text-[14px] font-medium border-amber-300 text-amber-700 hover:bg-amber-50"
          size="lg"
        >
          <ArrowPathIcon className="h-4 w-4 mr-2" />
          Re-render Preview
        </Button>
      ) : (
        <Button
          onClick={onSend}
          disabled={!canSend || isSending || !isReady}
          className={cn(
            'w-full h-11 text-[14px] font-medium shadow-sm',
            isReady &&
              'bg-emerald-600 hover:bg-emerald-700 text-white border-emerald-600',
          )}
          size="lg"
        >
          {isSending ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Sending...
            </>
          ) : (
            <>
              <PaperAirplaneIcon className="h-4 w-4 mr-2" />
              Send
            </>
          )}
        </Button>
      )}
    </div>
  );
};
